import { useCallback, useMemo, useState } from "react";

type ToggleExpansionResult<TItemId> = {
	isExpanded(itemId: TItemId): boolean;
	toggleExpansion(itemId: TItemId): void;
};

export const useToggleExpansion = <TItemId, >(initialExpandedItemIds?: readonly TItemId[]): ToggleExpansionResult<TItemId> => {
	const [expandedItemIds, setExpandedItemIds] = useState<Set<TItemId>>(
		() => {
			const ids: Set<TItemId> = new Set();

			if (!initialExpandedItemIds) {
				return ids;
			}

			for (const id of initialExpandedItemIds) {
				ids.add(id);
			}

			return ids;
		}
	);

	const isExpanded = useCallback(
		(itemId: TItemId): boolean => expandedItemIds.has(itemId),
		[expandedItemIds]
	);

	const toggleExpansion = useCallback(
		(itemId: TItemId): void => {
			setExpandedItemIds(ids => {
				const newIds: Set<TItemId> = new Set(ids);
				const isExpanded = newIds.has(itemId);

				if (isExpanded) {
					newIds.delete(itemId);
				} else {
					newIds.add(itemId);
				}

				return newIds;
			});
		},
		[]
	);

	return useMemo(
		(): ToggleExpansionResult<TItemId> => ({
			isExpanded,
			toggleExpansion
		}),
		[
			isExpanded,
			toggleExpansion
		]
	);
};